import {match} from 'ts-pattern';

import {vehiclesRoutes} from '@dms/routes';

import {composePath, type ApiError} from 'shared/web';

const READ_ACTIONS = ['openDetail', 'viewAsCustomer'] as const;

type Dependencies = {
  canWrite: boolean;
  canOrder: boolean;
  canRegisterExternal: boolean;
  vehicleId?: string;
  businessCaseId?: string;
  interestId?: string;
  navigate: (path: string) => void;
  copyConfiguration: (configurationId: string) => Promise<void>;
  deleteConfiguration: (configurationId: string) => Promise<void>;
  confirmDelete: (onConfirm: VoidFunction) => void;
  refreshData: VoidFunction;
  viewAsCustomer: (configurationId: string) => void;
  openOrder: (configurationId: string) => void;
  openRegisterExternal: (configurationId: string) => void;
  notifyLater: VoidFunction;
  notifyPermissionRequired: VoidFunction;
  handleError: (error: ApiError) => void;
  handleUnhandled: (actionKey: string) => void;
};

export function handleConfigurationGridAction(
  actionKey: string,
  configurationId: string,
  dependencies: Dependencies
) {
  const permissionGranted = match(actionKey)
    .with('order', () => dependencies.canOrder)
    .with('registerExternalOrder', () => dependencies.canRegisterExternal)
    .when(
      (key) => READ_ACTIONS.some((readAction) => readAction === key),
      () => true
    )
    .otherwise(() => dependencies.canWrite);
  if (!permissionGranted) {
    dependencies.notifyPermissionRequired();
    return;
  }

  const configuratorPath = composePath(vehiclesRoutes.newVehicleConfigurator, {
    params: {step: '5'},
    queryParams: {
      configurationId,
      businessCaseId: dependencies.businessCaseId,
      interestId: dependencies.interestId,
    },
  });

  match(actionKey)
    .with('openDetail', () =>
      dependencies.navigate(
        dependencies.vehicleId
          ? composePath(vehiclesRoutes.detail, {
              params: {id: dependencies.vehicleId},
              queryParams: {source: 'new-vehicle-configurations'},
            })
          : configuratorPath
      )
    )
    .with('openInConfigurator', () => dependencies.navigate(configuratorPath))
    .with('viewAsCustomer', () => dependencies.viewAsCustomer(configurationId))
    .with('order', () => dependencies.openOrder(configurationId))
    .with('registerExternalOrder', () => dependencies.openRegisterExternal(configurationId))
    .with('copyConfiguration', 'copyAsNewConfiguration', () =>
      dependencies
        .copyConfiguration(configurationId)
        .then(dependencies.refreshData)
        .catch(dependencies.handleError)
    )
    .with('deleteConfiguration', () =>
      dependencies.confirmDelete(() => {
        void dependencies
          .deleteConfiguration(configurationId)
          .then(dependencies.refreshData)
          .catch(dependencies.handleError);
      })
    )
    .with('discardVehicle', dependencies.notifyLater)
    .otherwise(dependencies.handleUnhandled);
}
